import Papa from "papaparse";
import type { ImportAccess } from "@/shared/import/import-group-access";
import { resolveImportGroup } from "@/shared/import/import-group-access";
import { type ImportGroup, importGroup } from "@/shared/import/import-groups";

/**
 * The downloadable people sample, built from the group the caller can
 * actually import (`resolveImportGroup`), so someone without payroll or
 * sensitive-edit access never gets a column that would fail their file.
 *
 * Keyed by catalogue field name rather than written as one CSV string, so a
 * field added to the catalogue without a sample column fails
 * `sample-csv.test.ts` instead of silently going missing from the sample.
 * Headers are the HRMS-style labels an export from another system carries,
 * and each one maps back onto its own field through `suggestMapping`. Entry
 * order is the column order of the file.
 *
 * The two rows are fake but pass the shore's validation: YYYY-MM-DD dates,
 * well-formed PAN, IFSC, UAN, ESI and MICR, `.example` emails. The second
 * person reports to the first, which is why the first holds the Manager role.
 *
 * `SAMPLE_BLANK_FIELDS` keep their header but no value. Both people are
 * active, so an exit date would contradict the status. A salary structure
 * must already exist by that name or the shore fails the row, and no name is
 * safe to assume; left blank, the workspace's default structure applies.
 */

export const SAMPLE_BLANK_FIELDS: ReadonlySet<string> = new Set([
  "resignationLetterDate",
  "relievingDate",
  "salaryStructureName",
]);

interface SampleColumn {
  readonly header: string;
  readonly values: readonly [string, string];
}

export const PEOPLE_SAMPLE_COLUMNS: Readonly<Record<string, SampleColumn>> = {
  employee_id: { header: "Employee Code", values: ["EMP-1001", "EMP-1002"] },
  first_name: { header: "First Name", values: ["Asha", "Rohan"] },
  last_name: { header: "Last Name", values: ["Rao", "Mehta"] },
  nickname: { header: "Nick Name", values: ["", "Ro"] },
  email: { header: "Work Email", values: ["asha.rao@acme.example", "rohan.mehta@acme.example"] },
  title: { header: "Job Title", values: ["Engineering Manager", "Software Engineer"] },
  department: { header: "Department", values: ["Engineering", "Engineering"] },
  manager_email: { header: "Reporting Manager Email", values: ["", "asha.rao@acme.example"] },
  role: { header: "Role", values: ["Manager", "Member"] },
  employment_type: { header: "Employment Type", values: ["Full Time", "Full Time"] },
  work_mode: { header: "Work Mode", values: ["Hybrid", "In Office"] },
  location: { header: "Location", values: ["Bengaluru", "Bengaluru"] },
  hire_date: { header: "Date of Joining", values: ["2022-04-01", "2024-07-15"] },
  designationName: { header: "Designation", values: ["Engineering Manager", "Software Engineer"] },
  gradeName: { header: "Grade", values: ["M1", "E2"] },
  confirmationDate: { header: "Confirmation Date", values: ["2022-10-01", "2025-01-15"] },
  noticeDays: { header: "Notice Period (Days)", values: ["60", "30"] },
  personalEmail: {
    header: "Personal Email",
    values: ["asha.personal@mail.example", "rohan.personal@mail.example"],
  },
  bloodGroup: { header: "Blood Group", values: ["A+", "O-"] },
  emergencyContactName: { header: "Emergency Contact Name", values: ["Vikram Rao", "Neha Mehta"] },
  emergencyContactPhone: {
    header: "Emergency Contact Number",
    values: ["+919800000001", "+919800000002"],
  },
  emergencyContactRelation: { header: "Emergency Contact Relation", values: ["Spouse", "Sibling"] },
  hrStatus: { header: "Employee Status", values: ["Active", "Active"] },
  resignationLetterDate: { header: "Resignation Date", values: ["", ""] },
  relievingDate: { header: "Last Working Day", values: ["", ""] },
  healthInsuranceProvider: {
    header: "Health Insurance Provider",
    values: ["Example Health", "Example Health"],
  },
  healthInsuranceNumber: {
    header: "Health Insurance Number",
    values: ["HI-100001", "HI-100002"],
  },
  workLocationState: { header: "Work State", values: ["KA", "KA"] },
  workLocationCountry: { header: "Work Country", values: ["IN", "IN"] },
  workLocationFrom: { header: "Work Location From", values: ["2022-04-01", "2024-07-15"] },
  annualCtc: { header: "Annual CTC", values: ["3600000.00", "1200000.00"] },
  salaryStructureName: { header: "Salary Structure", values: ["", ""] },
  ctcEffectiveFrom: { header: "CTC Effective From", values: ["2026-04-01", "2026-04-01"] },
  salaryMode: { header: "Payment Mode", values: ["Bank Transfer", "Bank Transfer"] },
  salaryCurrency: { header: "Salary Currency", values: ["INR", "INR"] },
  bankName: { header: "Bank Name", values: ["Example Bank", "Example Bank"] },
  accountHolderName: { header: "Name as per Bank", values: ["Asha Rao", "Rohan Mehta"] },
  bankAccountNumber: { header: "Bank Account No", values: ["50100123456789", "50100987654321"] },
  ifscCode: { header: "IFSC", values: ["EXMP0001234", "EXMP0001234"] },
  micrCode: { header: "MICR", values: ["560000001", "560000001"] },
  panNumber: { header: "PAN No", values: ["AAAPR1234A", "BBBPM5678B"] },
  providentFundAccount: {
    header: "PF No",
    values: ["KABNG00123450000001001", "KABNG00123450000001002"],
  },
  uanNumber: { header: "UAN No", values: ["100000000001", "100000000002"] },
  esiNumber: { header: "ESIC No", values: ["1000000001", "1000000002"] },
};

/// The sample for one resolved group: a header row and the two example rows,
/// holding only the columns that group offers.
export function buildSampleCsv(group: ImportGroup): string {
  const offered = new Set(group.fields.map((field) => field.name));
  const columns = Object.entries(PEOPLE_SAMPLE_COLUMNS)
    .filter(([name]) => offered.has(name))
    .map(([, column]) => column);
  return Papa.unparse(
    [
      columns.map((column) => column.header),
      columns.map((column) => column.values[0]),
      columns.map((column) => column.values[1]),
    ],
    { newline: "\n" },
  );
}

export function downloadPeopleSampleCsv(access: ImportAccess): void {
  const csv = buildSampleCsv(resolveImportGroup(importGroup("people"), access));
  const blob = new Blob([`${csv}\n`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "pulsewise-people-sample.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
